// Leader-line de-collision for numbered minimap markers.
//
// Numbered anomaly circles frequently pile up on top of each other on a small minimap, hiding
// numbers. This nudges overlapping *labels* (the numbered circles) apart with a simple, deterministic
// force relaxation while their *anchors* (the true positions) stay put. The caller then draws a thin
// leader line from each moved label back to its anchor dot — so every number is readable AND its real
// position is still visible.
//
// Coordinates are whatever square space the caller renders in (both callers use the 0..100 SVG/viewBox
// space, where 1 unit ≈ 1% of the tile). Pass each point already carrying screen-space `x`/`y`.
//
// Deterministic: no RNG, so identical inputs give identical output (no jitter while panning/zooming).
//
// Returns each input point spread through, plus:
//   lx, ly  — the de-collided label position (draw the numbered circle here)
//   moved   — true if the label was pushed far enough from its anchor to warrant a leader line
export function deCollideLabels(points, opts = {}) {
  const {
    minSep = 7,        // desired centre-to-centre spacing between labels (≈ marker diameter)
    iterations = 80,   // relaxation passes — more = tidier, but O(n²) per pass
    min = 4,           // keep labels inside [min, max] on both axes so they don't clip the tile
    max = 96,
    pull = 0.08,       // spring strength pulling each label back toward its anchor each pass
    movedThreshold = 0.9, // min anchor→label distance before a leader line is drawn
  } = opts;

  const nodes = points.map((p, i) => ({ p, i, ax: p.x, ay: p.y, x: p.x, y: p.y }));

  for (let it = 0; it < iterations; it++) {
    // Pairwise repulsion — push any two labels closer than minSep apart, equally.
    for (let a = 0; a < nodes.length; a++) {
      for (let b = a + 1; b < nodes.length; b++) {
        const na = nodes[a], nb = nodes[b];
        let dx = nb.x - na.x, dy = nb.y - na.y;
        let d = Math.hypot(dx, dy);
        if (d < 1e-3) {
          // Exactly coincident — separate along a deterministic golden-angle direction by index.
          const ang = na.i * 2.399963229728653;
          dx = Math.cos(ang); dy = Math.sin(ang); d = 1;
        }
        if (d < minSep) {
          const shift = (minSep - d) / 2;
          const ux = dx / d, uy = dy / d;
          na.x -= ux * shift; na.y -= uy * shift;
          nb.x += ux * shift; nb.y += uy * shift;
        }
      }
    }
    // Spring each label back toward its true position, then clamp inside the tile.
    for (const n of nodes) {
      n.x += (n.ax - n.x) * pull;
      n.y += (n.ay - n.y) * pull;
      n.x = Math.min(Math.max(n.x, min), max);
      n.y = Math.min(Math.max(n.y, min), max);
    }
  }

  return nodes.map((n) => ({
    ...n.p,
    lx: n.x,
    ly: n.y,
    moved: Math.hypot(n.x - n.ax, n.y - n.ay) > movedThreshold,
  }));
}
